import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, Play } from "lucide-react";
import { scrollToId } from "../lib/api";

export const HERO_VIDEO = "/videos/hero.mp4";
export const FILM_VIDEO = "/videos/film.mp4";
export const HERO_POSTER =
  "https://images.unsplash.com/photo-1765850257843-aa029ab7769c?crop=entropy&cs=srgb&fm=jpg&q=85";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-2 -mb-2">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { k: "Origin", v: "California Delta" },
  { k: "Method", v: "Small-Lot" },
  { k: "Harvest", v: "Picked by Hand" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden">
      <motion.div style={{ y: videoY }} className="absolute inset-0 scale-[1.12]">
        <video
          className="w-full h-full object-cover"
          src={HERO_VIDEO}
          poster={HERO_POSTER}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onCanPlay={(e) => e.target.play().catch(() => {})}
          data-testid="hero-video"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0908]/70 via-[#0A0908]/35 to-[#0A0908]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0908]/60 via-transparent to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="overline-label mb-6"
        >
          Delta Breeze Winery — Est. California Delta
        </motion.p>

        <h1 className="font-serif-display font-light text-[#F4F1EA] text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight">
          <MaskedLine delay={0.55}>Craft from</MaskedLine>
          <MaskedLine delay={0.7}>
            <span className="italic text-[#D4AF37]">soil</span> to glass.
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 max-w-xl text-base sm:text-lg text-[#A39E93] leading-relaxed"
        >
          Small-lot wines shaped by Delta soils and cool evening air — picked by hand,
          fermented with patience, bottled when the blend feels honest.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.25, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-join"
            onClick={() => scrollToId("club")}
            className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D4AF37] text-[#0A0908] px-8 py-4 hover:bg-[#F4F1EA] transition-colors duration-300"
          >
            Join the Club
          </button>
          <button
            data-testid="hero-play-video-button"
            onClick={() => scrollToId("film")}
            className="group font-mono text-xs uppercase tracking-[0.25em] text-[#F4F1EA] px-6 py-4 border border-[rgba(244,241,234,0.25)] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300 flex items-center gap-3"
          >
            <Play size={13} className="fill-current" /> Watch the Film
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-14 pt-6 border-t border-[rgba(212,175,55,0.15)] grid grid-cols-3 gap-4 max-w-xl"
        >
          {STATS.map((s) => (
            <div key={s.k} data-testid={`hero-stat-${s.k.toLowerCase().replace(/\s/g, "-")}`}>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#6E685E]">{s.k}</p>
              <p className="font-serif-display text-lg text-[#F4F1EA] mt-1">{s.v}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToId("story")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-6 right-6 lg:right-10 z-10 text-[#6E685E] hover:text-[#D4AF37] transition-colors"
        aria-label="Scroll down"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }} className="block">
          <ChevronDown size={22} />
        </motion.span>
      </motion.button>
    </section>
  );
};
